import { useState } from 'react';

import { AutoOutreachPerformance } from '@apps/web/components/admin/outreach_observability/auto_outreach_performance';
import { CaregiverHistogram } from '@apps/web/components/admin/outreach_observability/caregiver_histogram';
import {
    echoedOutreachWindow,
    OUTREACH_ANALYTICS_TIMEZONE,
    OUTREACH_REFRESH_INTERVAL_MS,
    type OutreachTabProps,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { OutcomeTrendChart } from '@apps/web/components/admin/outreach_observability/outcome_trend_chart';
import { OutreachFunnel } from '@apps/web/components/admin/outreach_observability/outreach_funnel';
import {
    type KpiRunMetric,
    RunsDrillDialog,
    type RunsDrillFilters,
} from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { SchedulerFollowUpDistributionChart } from '@apps/web/components/admin/outreach_observability/scheduler_follow_up_distribution_chart';
import {
    DEFAULT_WEEKLY_WEEKS,
    WeeklyLaneComparison,
} from '@apps/web/components/admin/outreach_observability/weekly_lane_comparison';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { formatDateKey } from '@apps/web/lib/date_utils';
import { useAdminOutreachObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type RunsDrill = {
    readonly title: string;
    readonly description?: string;
    readonly metric: KpiRunMetric;
    readonly filters?: RunsDrillFilters;
};

export function OutcomesTab({
    organizationIds,
    window: liveWindow,
    urgency,
}: OutreachTabProps) {
    const [weeklyWeeks, setWeeklyWeeks] = useState(DEFAULT_WEEKLY_WEEKS);
    const { data: responseData } = useAdminOutreachObservability(
        {
            sections: [
                'live',
                'trend',
                'comparisons',
                'auto_outreach',
                'auto_outreach_weekly',
            ],
            organizationIds,
            window: liveWindow,
            urgency,
            weeklyWeeks,
        },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;
    const activeWindow =
        (data ? echoedOutreachWindow(data) : null) ?? liveWindow;
    const windowSummary = outreachWindowSummary(activeWindow);
    const [drill, setDrill] = useState<RunsDrill | null>(null);
    return (
        <div className="flex w-full max-w-full min-w-0 flex-col gap-4">
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Outreach funnel</CardTitle>
                    <CardDescription>
                        Pipeline from created to filled ({windowSummary}) — each
                        stage shows its share of the prior stage and of all
                        runs; the drop between stages is where outreaches are
                        lost.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    {data ? (
                        <OutreachFunnel
                            pulse={data.pulse}
                            comparisons={data.comparisons ?? null}
                            kpiHourlyTrends={data.kpi_hourly_trends}
                            window={activeWindow}
                            onStageClick={(stage) =>
                                setDrill({
                                    title: `${stage.label} runs`,
                                    description: `Runs reaching the ${stage.label.toLowerCase()} funnel stage (${windowSummary}).`,
                                    metric: stage.metric,
                                    filters: { window: activeWindow },
                                })
                            }
                        />
                    ) : (
                        <div className="text-muted-foreground flex h-40 items-center justify-center rounded-md border text-sm">
                            Loading…
                        </div>
                    )}
                </CardContent>
            </Card>
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Daily outcomes</CardTitle>
                    <CardDescription>
                        Outreach runs per day (America/New_York days) broken
                        down by outcome bucket.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    <OutcomeTrendChart
                        days={data?.trend ?? []}
                        onDayClick={(day) =>
                            setDrill({
                                title: `Runs on ${formatDateKey(
                                    day,
                                    OUTREACH_ANALYTICS_TIMEZONE,
                                )}`,
                                metric: 'runs',
                                filters: { day },
                            })
                        }
                    />
                </CardContent>
            </Card>
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Outreach performance by lane</CardTitle>
                    <CardDescription>
                        Response and per-shift outcomes for auto-drafted, agent,
                        and manual outreaches ({windowSummary};
                        America/New_York), including their auto-start and
                        auto-monitor adoption. Shift fills are attributed via
                        outreach metadata, so counts can differ slightly from
                        the run-based charts below.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    <AutoOutreachPerformance
                        section={data?.auto_outreach ?? null}
                    />
                </CardContent>
            </Card>
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Week by week</CardTitle>
                    <CardDescription>
                        Trailing {weeklyWeeks} ET weeks (Monday-start, current
                        week partial) for the selected lanes, with the change
                        from the prior week under each value. The organization
                        selector above narrows this view; only the window
                        selector is ignored, since this view spans its own
                        trailing window.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    <WeeklyLaneComparison
                        section={data?.auto_outreach_weekly ?? null}
                        weeks={weeklyWeeks}
                        onWeeksChange={setWeeklyWeeks}
                    />
                </CardContent>
            </Card>
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Scheduler follow-up distribution</CardTitle>
                    <CardDescription>
                        Agent outreach runs grouped by scheduler follow-up count
                        ({windowSummary}) and broken down by outcome bucket.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    <SchedulerFollowUpDistributionChart
                        buckets={data?.scheduler_follow_up_distribution ?? []}
                        onBucketClick={(point) =>
                            setDrill({
                                title:
                                    point.followUpCountMax === null
                                        ? `Runs with ${point.followUpCountMin}+ scheduler follow-ups`
                                        : `Runs with ${point.followUpCountMin} scheduler ${
                                              point.followUpCountMin === 1
                                                  ? 'follow-up'
                                                  : 'follow-ups'
                                          }`,
                                description: `Scheduler follow-up distribution runs (${windowSummary}).`,
                                metric: 'runs',
                                filters: {
                                    schedulerFollowUpCountMin:
                                        point.followUpCountMin,
                                    schedulerFollowUpCountMax:
                                        point.followUpCountMax,
                                    window: activeWindow,
                                },
                            })
                        }
                    />
                </CardContent>
            </Card>
            <Card className="max-w-full min-w-0 overflow-hidden">
                <CardHeader>
                    <CardTitle>Suggested caregiver distribution</CardTitle>
                    <CardDescription>
                        Suggested caregivers per outreach ({windowSummary}) —
                        the red bar is runs with zero suggestions.
                    </CardDescription>
                </CardHeader>
                <CardContent className="max-w-full min-w-0">
                    <CaregiverHistogram
                        buckets={data?.histogram ?? []}
                        onBucketClick={(point) =>
                            setDrill({
                                title: point.isZeroBucket
                                    ? 'Zero-suggestion runs'
                                    : `Runs with ${point.suggestedCount} suggested caregivers`,
                                description: `Suggested-caregiver distribution runs (${windowSummary}).`,
                                metric: 'runs',
                                filters: {
                                    suggestedCaregiverCount:
                                        point.suggestedCount,
                                    window: activeWindow,
                                },
                            })
                        }
                    />
                </CardContent>
            </Card>
            <RunsDrillDialog
                open={drill !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDrill(null);
                    }
                }}
                title={drill?.title ?? ''}
                description={drill?.description}
                metric={drill?.metric ?? 'runs'}
                organizationIds={organizationIds}
                urgency={urgency}
                filters={drill?.filters}
            />
        </div>
    );
}
